import {
  WalletDetailsApiData,
  WalletDetailsApiResponse,
  WalletDetailsViewModel,
  WalletLedgerPending
} from '@/ducks/billing/billing.types'
import { parseWalletMoney } from '@/ducks/billing/billing.wallet-funding'

const DEFAULT_VIEW_MODEL: WalletDetailsViewModel = {
  balance: 0,
  currency: '',
  walletSource: 'unknown'
}

const toFiniteNumber = (value: unknown) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return undefined
}

const normalizeWalletSource = (
  data?: WalletDetailsApiData
): WalletDetailsViewModel['walletSource'] => {
  if (data?.walletSource === 'store-wallet') return 'store-wallet'
  if (data?.walletSource === 'fs') return 'fs'
  return 'unknown'
}

const normalizeLedgerPending = (data?: WalletDetailsApiData) => {
  if (data?.walletSource !== 'store-wallet') return undefined
  return data.ledger?.pending
}

export const normalizeWalletDetailsData = (
  data?: WalletDetailsApiData
): WalletDetailsViewModel => {
  if (!data) return { ...DEFAULT_VIEW_MODEL }

  const normalized: WalletDetailsViewModel = {
    balance: toFiniteNumber(data.balance) ?? DEFAULT_VIEW_MODEL.balance,
    currency: typeof data.currency === 'string' ? data.currency : '',
    walletSource: normalizeWalletSource(data)
  }

  if (typeof data.active === 'boolean') {
    normalized.active = data.active
  }

  const ledgerPending = normalizeLedgerPending(data)
  if (ledgerPending) {
    normalized.ledgerPending = ledgerPending
  }

  if (data.walletSource === 'store-wallet') {
    if (typeof data.enrolled === 'boolean') {
      normalized.enrolled = data.enrolled
    }
    if (typeof data.accountExists === 'boolean') {
      normalized.accountExists = data.accountExists
    }
  }

  return normalized
}

export const normalizeWalletDetailsResponse = (
  response: WalletDetailsApiResponse
): WalletDetailsViewModel => {
  return normalizeWalletDetailsData(response.data)
}

// Pending card = accepted orders not yet picked up. Cash-out in flight
// already left Settled and must not be added here.
export const sumWalletLedgerPending = (
  pending?: WalletLedgerPending
): number => {
  if (!pending) return 0

  return Math.max(0, parseWalletMoney(pending.orderPendingSecured) ?? 0)
}
